import { AvailabilityEnum } from '../../../products/ProductTypes';
import { OrderProductDeliveryWindows } from '../../../products/stock/future-stock-v2/utils/dtos/order-product-delivery-windows.dto';
import { PreOrderAvailability, isPreOrder } from '../../../products/utils/availability.utils';
import { UpdateDeliveryWindowPayload } from '../interfaces/add-order-product-form-state.types';
import {
  AddOrderProductColorsFormState,
  OrderProductGridItem,
  UpdateItemFormat,
} from '../orderProducts.types';
import { DeliveryWindowAvailability } from './deliveryWindowSelect.types';

interface BuildOrderItemsToCreateParams {
  createOrderItems: UpdateItemFormat[];
  grid: OrderProductGridItem[];
  availability: AvailabilityEnum;
  deliveryWindowId?: string;
}

export const buildOrderItemsToCreate = ({
  createOrderItems,
  grid,
  availability,
  deliveryWindowId,
}: BuildOrderItemsToCreateParams): UpdateItemFormat[] => {
  const hasQuantity = createOrderItems.some((orderItem) => (orderItem.quantity ?? 0) > 0);

  if (hasQuantity) {
    return createOrderItems;
  }

  const [firstGridItem] = grid;

  if (!firstGridItem) {
    return [];
  }

  return [
    {
      skuId: firstGridItem.skuId,
      quantity: 0,
      availability,
      deliveryWindowId,
    },
  ];
};

const DELIVERY_WINDOWS_KEY_BY_AVAILABILITY: Record<
  PreOrderAvailability,
  keyof OrderProductDeliveryWindows
> = {
  [AvailabilityEnum.INFINITE_PRE_ORDER]: 'infinitePreOrderDeliveryWindows',
  [AvailabilityEnum.MATERIAL_PRE_ORDER]: 'materialPreOrderDeliveryWindows',
  [AvailabilityEnum.FUTURE_STOCK]: 'futureStockDeliveryWindows',
};

interface FindSearchedDeliveryWindowParams {
  windows: OrderProductDeliveryWindows;
  availability: AvailabilityEnum;
  deliveryWindowId?: string;
}

export const findSearchedDeliveryWindowAvailability = ({
  windows,
  availability,
  deliveryWindowId,
}: FindSearchedDeliveryWindowParams): DeliveryWindowAvailability | undefined => {
  if (!isPreOrder(availability)) {
    return undefined;
  }

  const deliveryWindow = windows[DELIVERY_WINDOWS_KEY_BY_AVAILABILITY[availability]]?.find(
    (window) => window.id === deliveryWindowId,
  );

  return deliveryWindow ? { availability, deliveryWindow } : undefined;
};

export const getUpdateDeliveryWindowPayload = (
  dwAvailability?: DeliveryWindowAvailability,
): UpdateDeliveryWindowPayload => {
  if (dwAvailability && isPreOrder(dwAvailability.availability)) {
    return {
      availability: dwAvailability.availability,
      deliveryWindow: dwAvailability.deliveryWindow,
    };
  }

  return {
    availability: AvailabilityEnum.READY_FOR_DELIVERY,
  };
};

export const isSameAddProduct = (
  product1: AddOrderProductColorsFormState,
  product2: AddOrderProductColorsFormState,
) => {
  const isSameProductId = product1.product?.id === product2.product?.id;
  const isSameProductColorId = product1.color?.productColorId === product2.color?.productColorId;
  const isSameAvailability = product1.availability === product2.availability;
  const isSameDeliveryWindowId = product1.deliveryWindow?.id === product2.deliveryWindow?.id;
  const isSameHash = product1?.hash === product2?.hash;

  return (
    isSameProductId &&
    isSameProductColorId &&
    isSameAvailability &&
    isSameDeliveryWindowId &&
    isSameHash
  );
};
